import i18n from 'i18next';
import React, {useState, useEffect} from 'react';
import {Manage} from "@/Pages/Manage/Manage";
import Authenticated from "@/Layouts/Authenticated";
import {Card} from "@/Components/Card";
import "@/../sass/authenticated/adminaccountoverview.scss";
import {usePage} from "@inertiajs/inertia-react";
import {ButtonPrimary} from "@/Components/Buttons/ButtonPrimary";
import {ButtonSecondary} from "@/Components/Buttons/ButtonSecondary";

const Usage = () => {
    const props = usePage().props ? usePage().props : null;
    const trackedDates = props ? props.trackedDates : [];
    const [trackingData, setTrackingData] = useState([]);
    const [selectedDate, setSelectedDate] = useState(trackedDates.length > 0 ? trackedDates[0] : null);

    const getMonthName = (monthNumber) => {
        // The year is not important
        const date = new Date(2024, monthNumber - 1);
        return new Intl.DateTimeFormat(i18n.language, {month: 'long'}).format(date);
    }

    const handleSelectChange = (e) => {
        const [month, year] = e.target.value.split("_");
        setSelectedDate({month: month, year: year});
        loadPointInTimeData(month, year);
    };

    const loadPointInTimeData = async (month, year) => {
        const response = await axios.get(route('manage.get-usage', [year, month]));
        if (response?.status === 200 && response?.data) {
            setTrackingData(response.data);
        } else {
            console.error('Failed to load point in time data', response);
        }
    }

    const onExportClick = async () => {
        let month = selectedDate.month;
        let year = selectedDate.year;
        const response = await axios.get(route('manage.usage-csv', [year, month]));
        if (response?.status === 200 && response?.data) {
            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv;charset=utf-8;' }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `usage_${month}_${year}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } else{
            console.error('Failed to export CSV', response);
        }
    }

    const onBackClick = () => {
        window.history.back();
    }

    useEffect(() => {
        if (selectedDate){
            loadPointInTimeData(selectedDate.month, selectedDate.year);
        }
    }, [selectedDate]);

    return (
        <div>
            <Manage title={i18n.t('usage.overview')}>
                <div className="row">
                    <Card className="usage-info-card col">
                        <div className="usage-info-container">
                            <div className="usage-card-header row">
                                <div className="col-4">
                                    <span className="usage-card-title" data-testid={"usage-span-title"}>
                                        {i18n.t('usage.usage')}
                                    </span>
                                </div>
                                <form className="col-3">
                                    <select onChange={handleSelectChange}
                                            className="select form-control form-control-bordered form-input"
                                            data-testid={"usage-select-filter_date"}
                                    >
                                        {trackedDates.map((date, index) => {
                                            return (
                                                <option key={index} value={date.month + "_" + date.year}>
                                                    {getMonthName(date.month)} {date.year}
                                                </option>
                                            )
                                        })}
                                    </select>
                                </form>
                            </div>
                            <div style={{position: 'relative', top: '22px'}}>
                                {trackingData.map((data, index) => {
                                    let moduleName = data.module_description ? `<span title="${data.module_description}">${data.module_name}&nbsp;&nbsp;<i className="fas fa-question-circle"></i></span>` : data.module_name;

                                    return (
                                        <div className="row mb-5" key={index}>
                                            {/* FIRST PART: ICON + MODULE NAME */}
                                            <div className="col-md-4 d-flex align-items-center">
                                                <div className="d-flex align-items-center">
                                                    {/* ICON */}
                                                    <div className="module-svg" data-testid={"usage-div-module_icon"}
                                                         style={{
                                                             backgroundColor: data.module_colors.color ? data.module_colors.color : "black",
                                                             width: "64px"
                                                         }} dangerouslySetInnerHTML={{__html: data.module_icon}}/>

                                                    {/* MODULE NAME */}
                                                    <div className="usage-module_name" data-testid={"usage-div-module_name"}
                                                         dangerouslySetInnerHTML={{__html: moduleName}}></div>
                                                </div>
                                            </div>
                                            {/* SECOND PART: PROGRESS BAR AND METRICS */}
                                            <div className="col-md-8" style={{position: "relative", top: "30px"}}>
                                                {
                                                    Object.entries(data.usageTracking).map(([metric, usageData], usageDataIndex) => {
                                                        let usagePercentage = usageData.usage_percentage + "%";
                                                        let progressBarCustomClass = "";
                                                        if (usageData.usage_percentage < 0 || usageData.usage_percentage == null) {
                                                            usagePercentage = "0%";
                                                        }
                                                        if (usageData.usage_percentage > 100) {
                                                            usagePercentage = "100%";
                                                            progressBarCustomClass = "bg-danger";
                                                        }
                                                        return (
                                                            <div className="row mb-2" key={usageDataIndex}>
                                                                <div className="col-10">
                                                                    <div className="progress-bar-container mb-5">
                                                                        <div
                                                                            className="progress-bar-title"
                                                                            data-testid={"usage-div-metric_name"}>{metric}</div>
                                                                        <div className={`progress-bar-fill ${progressBarCustomClass}`}
                                                                             data-testid={"usage-div-usage_percentage"}
                                                                             style={{width: usagePercentage}}></div>
                                                                    </div>
                                                                </div>
                                                                <div className="col-2 usage-metric-numbers"
                                                                     data-testid={"usage-div-metric_numbers"}>
                                                                    {usageData.usage_count != "0" && usageData.usage_count != null ?
                                                                        usageData.usage_count : "0"
                                                                    }
                                                                    {usageData.limit != "0" && usageData.limit != null ?
                                                                        " / " + usageData.limit : ""
                                                                    }
                                                                </div>
                                                            </div>
                                                        )
                                                    })
                                                }
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                            <div className="row" style={{ position: "relative", top: "40px" }}>
                                <div className="ms-auto col-auto d-flex">
                                    <div className="me-2">
                                        <ButtonSecondary onClick={onBackClick} data-testid={"usage-button-export_csv"}>
                                            <span>{i18n.t('usage.back')}</span>
                                        </ButtonSecondary>
                                    </div>
                                    <div>
                                        <ButtonPrimary onClick={onExportClick} data-testid={"usage-button-export_csv"}>
                                            <i className="fas fa-download"></i>
                                            &nbsp;&nbsp;
                                            <span>{i18n.t('usage.export-csv')}</span>
                                        </ButtonPrimary>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Card>

                </div>
            </Manage>
        </div>
    )
}

Usage.layout = page =>
    <Authenticated children={page}></Authenticated>
export default Usage
